import { useMutation } from '@tanstack/react-query';

import type { IndeedSendAttemptPayload, NotifyCandidateMetadata } from 'clients/CampaignsClient/CampaignsClient.types';
import type { IndeedJobContact } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';
import type { ContactCandidateData } from '../useContactCandidateMutation.types';
import type { IndeedConversation } from '../utils/findIndeedConversationInHistory';

import { IndeedCandidateAlreadyContactedException } from 'shared/legacy/exceptions/IndeedCandidateAlreadyContactedException';
import { useIndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedConfig';
import { useIndeedReplyMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedReplyMessageMutation';
import { useIndeedSendMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedSendMessageMutation';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { extractEsPersonId } from '../utils/extractEsPersonId';
import { findIndeedConversationInHistory } from '../utils/findIndeedConversationInHistory';
import { useDiscoverIndeedConversation } from './useDiscoverIndeedConversation';

export const useContactIndeedMutation = () => {
  const { data: currentUser } = useCurrentUserQuery();
  const indeedConfig = useIndeedConfig();

  const indeedReplyMessageMutation = useIndeedReplyMessageMutation();
  const indeedSendMessageMutation = useIndeedSendMessageMutation();
  const discoverIndeedConversation = useDiscoverIndeedConversation();
  const { isFeatureToggleEnabled } = useFeatureToggles();

  return useMutation(async (params: ContactCandidateData<'indeed'>) => {
    if (!currentUser) {
      throw new Error('Current user not found');
    }

    let metadata: NotifyCandidateMetadata | undefined = undefined;

    let queued = false;
    let indeedSendAttemptPayload: IndeedSendAttemptPayload | undefined;
    const {
      campaignId,
      esPersonId,
      subject,
      message,
      indeedProjectId,
      indeedJob,
      isReply,
      metadata: indeedMetadata,
      candidateName,
      improvedJobPreview,
      job_improved_id: jobImprovedId,
    } = params;
    const esPersonIdWithoutPlatform = extractEsPersonId(esPersonId);

    const replyOnConversation = ({ conversationId, jobSeekerKey }: IndeedConversation) =>
      indeedReplyMessageMutation.mutateAsync({ message, conversationId, jobSeekerKey });

    const discoverConversation = () =>
      discoverIndeedConversation({ candidateId: esPersonIdWithoutPlatform, candidateName });

    let conversation: IndeedConversation | null =
      isReply && indeedMetadata?.conversation_id && indeedMetadata?.job_seeker_key
        ? { conversationId: indeedMetadata.conversation_id, jobSeekerKey: indeedMetadata.job_seeker_key }
        : findIndeedConversationInHistory(esPersonId, indeedConfig.email);

    if (!conversation && !indeedProjectId) {
      conversation = await discoverConversation();
    }

    if (conversation) {
      await replyOnConversation(conversation);
    } else {
      if (!indeedProjectId) throw TypeError('indeedProjectId must be provided');

      const isImprovedJobPreviewEnabled = isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB);

      const job: IndeedJobContact | null | undefined =
        isImprovedJobPreviewEnabled && improvedJobPreview
          ? { name: indeedJob?.name, description: improvedJobPreview }
          : indeedJob;

      if (campaignId) {
        indeedSendAttemptPayload = {
          subject,
          message,
          sender_name: currentUser.mailSignOff.fullName,
          sender_company: currentUser.tenant.name,
          job_title: subject,
          job_improved_id: jobImprovedId ? Number(jobImprovedId) : undefined,
          project_key: indeedProjectId,
          job,
        };

        metadata = {
          job_info: {
            jobTitle: indeedJob?.name,
          },
        };
        queued = true;
      } else {
        try {
          await indeedSendMessageMutation.mutateAsync({
            candidateId: esPersonIdWithoutPlatform,
            senderName: currentUser.mailSignOff.fullName,
            senderCompany: currentUser.tenant.name,
            jobTitle: subject,
            message,
            projectKey: indeedProjectId,
            job,
          });

          metadata = {
            job_info: {
              jobTitle: indeedJob?.name,
            },
          };
        } catch (error) {
          if (!(error instanceof IndeedCandidateAlreadyContactedException)) throw error;

          const existingConversation = await discoverConversation();

          if (!existingConversation) throw error;

          await replyOnConversation(existingConversation);
        }
      }
    }

    const sanitizedName = candidateName.split(' ').join('');
    const encodedName = encodeURIComponent(sanitizedName).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    );
    const base64Name = btoa(encodedName);

    const thread_id = `${currentUser.tenant.id}_${base64Name}`;

    return { thread_id, metadata, queued, indeedSendAttemptPayload };
  });
};
